import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import axiosBaseConfig from "../api/axios";
import MainLayout from "../layouts/MainLayout";
import UsersTable from "../components/UsersPage";
import { useQuery } from "@apollo/client";
import Login from "./Login";
import SuperAdmin from "../components/SuperAdmin";
import TrafficSources from "../components/TrafficSources";
import Campaign from "../components/Campaign";
import PriceSettingsTab from "../components/PriceSettings";
import {
  GET_DEFAULT_PRICES,
  GET_DEFAULT_PRICES_WITH_LIMITS,
} from "../api/graphql/users";
import CampaignSuperAdmin from "../components/CampaignSuperadmin";
import ViewCampaign from "../components/Campaign/view-campaign";
import ModerationPage from "../components/Moderation";
import IdfaReports from "../components/IdfaReports";
import AdminLogs from "../components/AdminLogs";
import CryptoPayments from "../components/CryptoPayments";
import NotFound from "./NotFound";
import PrivateRoute from "../components/guards/PrivateRoute";
import SuperAdminRoute from "../components/guards/SuperAdminRoute";
import PublicRoute from "../components/guards/PublicRoute";
import Logout from "../components/Logout";
import ViewUser from "../components/UsersPage/ViewUser";
import LoadingSpinner from "../components/LoadingSpinner";

const ProjectRoutes = () => {
  const authRoutes = ["login", "registration", "reset-password"];
  // useQuery(GET_ADMIN_PROFILE);

  const limitsCacheKey = "promobileAdmin:supportsUserPricingLimits";
  const limitsCacheTtlMs = 7 * 24 * 60 * 60 * 1000;

  const readCachedLimitsSupport = () => {
    try {
      const raw = window.localStorage.getItem(limitsCacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const value = Boolean(parsed?.value);
      const ts = Number(parsed?.ts);
      if (!Number.isFinite(ts)) return null;
      if (Date.now() - ts > limitsCacheTtlMs) return null;
      return value;
    } catch {
      return null;
    }
  };

  const writeCachedLimitsSupport = (value: boolean) => {
    try {
      window.localStorage.setItem(
        limitsCacheKey,
        JSON.stringify({ value, ts: Date.now() }),
      );
    } catch {
      return;
    }
  };

  const [useLegacyPricingQuery, setUseLegacyPricingQuery] = React.useState(() => {
    const cached = readCachedLimitsSupport();
    return cached === false;
  });

  const defaultPricesWithLimits = useQuery(GET_DEFAULT_PRICES_WITH_LIMITS, {
    skip: useLegacyPricingQuery,
  });

  const defaultPricesLegacy = useQuery(GET_DEFAULT_PRICES, {
    skip: !useLegacyPricingQuery,
  });

  const isLimitsUnsupportedError = (err: any) => {
    const graphQLErrors = err?.graphQLErrors;
    const messages: string[] = Array.isArray(graphQLErrors)
      ? graphQLErrors.map((e: any) => String(e?.message || ""))
      : [String(err?.message || "")];

    return messages.some((message) => {
      return (
        message.includes('Cannot query field "LIMITS"') ||
        message.includes("Cannot query field 'LIMITS'") ||
        message.includes("Cannot query field LIMITS")
      );
    });
  };

  useEffect(() => {
    if (
      defaultPricesWithLimits.error &&
      isLimitsUnsupportedError(defaultPricesWithLimits.error)
    ) {
      writeCachedLimitsSupport(false);
      setUseLegacyPricingQuery(true);
    }
  }, [defaultPricesWithLimits.error]);

  useEffect(() => {
    if (!useLegacyPricingQuery && defaultPricesWithLimits.data) {
      writeCachedLimitsSupport(true);
    }
  }, [useLegacyPricingQuery, defaultPricesWithLimits.data]);

  const activeDefaultPricesQuery = useLegacyPricingQuery
    ? defaultPricesLegacy
    : defaultPricesWithLimits;

  const { data: defaultPrices, refetch, loading, error } = activeDefaultPricesQuery;

  useEffect(() => {
    const securedRoute = authRoutes.some((route) =>
      window.location.href.includes(route),
    );

    if (securedRoute) {
      (async () => await axiosBaseConfig.get("auth/logout"))();
    }
  }, [window.location.href, location]);

  if (
    defaultPricesWithLimits.error &&
    isLimitsUnsupportedError(defaultPricesWithLimits.error) &&
    !useLegacyPricingQuery
  ) {
    return <LoadingSpinner />;
  }

  if (loading) return <LoadingSpinner />;
  if (error)
    return (
      <p style={{ margin: 0, padding: "var(--ds-padding-xl)" }}>
        Failed to load pricing
      </p>
    );

  return (
    <Routes>
      {/* Private Routes */}
      <Route element={<PrivateRoute />}>
        <Route element={<MainLayout />}>
          <Route path="/user/:id" element={<ViewUser />} />
          <Route element={<SuperAdminRoute />}>
            <Route path="/superadmin-panel" element={<SuperAdmin />} />
            <Route
              path="/campaigns-super-admin"
              element={<CampaignSuperAdmin />}
            />
            <Route path="/logs" element={<AdminLogs />} />
          </Route>
          <Route path="/traffic-sources" element={<TrafficSources />} />
          <Route path="/campaigns" element={<Campaign />} />
          <Route path="/moderation" element={<ModerationPage />} />
          <Route
            path="/campaigns/:orderType/:orderId"
            element={<ViewCampaign />}
          />
          <Route path="/idfa-reports" element={<IdfaReports />} />
          <Route path="/crypto-payments" element={<CryptoPayments />} />
          <Route
            path="/price-settings"
            element={
              <PriceSettingsTab
                priceSettings={defaultPrices?.getDefaultPricing}
                refetch={refetch}
              />
            }
          />
          <Route path="/logout" element={<Logout />} />
          <Route path="*" element={<UsersTable />} />
        </Route>
      </Route>
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<Login />} />
      </Route>
    </Routes>
  );
};

export default ProjectRoutes;
